// P2 · "How this telling leans" — distribution-relative reading companion. One card
// per numeric axis: lean phrase, evidence quote, a mini gauge (target marker vs the
// other outlets' dots vs the codebook midpoint), and descriptive copy. Tapping a card
// flashes the matching span in P1. Copy is descriptive, never "this is biased" (spec §7).

import type { CSSProperties } from "react";
import type { Bundle } from "../types";
import { INK, kindColor, pos, sectionLabel } from "../style";
import { outletDisplay } from "../labels";

export function AxisProfile({ b, flashAxis, onLeanTap }: {
  b: Bundle; flashAxis: string | null; onLeanTap: (axis: string) => void;
}) {
  const T = b.target;
  const items = b.axis_profile.filter((a) => !a.dim);
  const missing = b.axis_profile.find((a) => a.dim);
  const otherOutlets = (b.incident.outlets || []).filter((o) => o !== T.outlet).length;

  return (
    <>
      <div style={{ ...sectionLabel, marginBottom: 3 }}>How this telling leans</div>
      <p style={{ fontSize: 11.5, color: "rgba(55,53,47,.5)", lineHeight: 1.45, margin: "0 0 13px" }}>
        Next to the other {otherOutlets} outlet{otherOutlets === 1 ? "" : "s"} on this story. Tap any item to find it in the article.
      </p>

      <div style={{ background: "#f7f6f3", border: "1px solid rgba(55,53,47,.1)", borderRadius: 7, padding: "13px 14px", marginBottom: 14 }}>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: INK }}>{b.lean_summary}</p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {items.map((a) => {
          const kind = a.evidence_kind || "axis";
          const c = kindColor(kind);
          const active = flashAxis === a.axis;
          const tl = pos(a.target_value, a.scale);
          const dots = a.per_outlet.filter((o) => o.outlet !== T.outlet).map((o) => ({
            left: pos(o.value, a.scale) + "%",
            title: outletDisplay(o.outlet) + ": " + (o.value > 0 ? "+" : "") + o.value,
          }));
          const cardStyle: CSSProperties = {
            textAlign: "left", width: "100%", background: "#fff",
            border: "1px solid " + (active ? "rgba(55,53,47,.5)" : "rgba(55,53,47,.12)"),
            borderRadius: 7, padding: "11px 12px", cursor: "pointer", fontFamily: "inherit",
            transition: "border-color .12s, box-shadow .12s", boxShadow: active ? "0 0 0 2px rgba(55,53,47,.07)" : "none",
          };
          return (
            <button key={a.axis} onClick={() => onLeanTap(a.axis)} style={cardStyle}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 7 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: INK, lineHeight: 1.3 }}>{a.lean_label}</span>
                <span style={{ fontSize: 10, fontWeight: 600, whiteSpace: "nowrap", color: active ? INK : "rgba(55,53,47,.42)" }}>{active ? "Showing ↘" : "Show in text ↘"}</span>
              </div>
              {a.evidence_quote && (
                <div style={{ display: "flex", gap: 7, alignItems: "flex-start", marginBottom: 9 }}>
                  <span style={{ flex: "none", fontSize: 11, lineHeight: 1.25, color: c.fg }}>{kind === "device" ? "◆" : "▲"}</span>
                  <span style={{ fontFamily: "'Source Sans 3',serif", fontStyle: "italic", fontSize: 12.5, lineHeight: 1.4, color: "rgba(55,53,47,.72)" }}>“…{a.evidence_quote}…”</span>
                </div>
              )}
              <div style={{ position: "relative", height: 15, margin: "0 2px 5px" }}>
                <div style={{ position: "absolute", top: 7, left: 0, right: 0, height: 2, background: "rgba(55,53,47,.1)", borderRadius: 2 }} />
                <div style={{ position: "absolute", top: 3, left: pos(a.midpoint, a.scale) + "%", width: 2, height: 9, background: "rgba(55,53,47,.22)", transform: "translateX(-1px)" }} />
                {dots.map((d, i) => (
                  <div key={i} title={d.title} style={{ position: "absolute", top: 4.5, left: d.left, transform: "translateX(-50%)", width: 8, height: 8, borderRadius: "50%", background: "#fff", border: "1.5px solid rgba(55,53,47,.35)" }} />
                ))}
                <div title="This article" style={{ position: "absolute", top: 1, left: tl + "%", transform: "translateX(-50%) rotate(45deg)", width: 11, height: 11, background: INK, borderRadius: 2, boxShadow: "0 1px 2px rgba(15,15,15,.18)", zIndex: 2 }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "rgba(55,53,47,.36)", margin: "0 2px 7px", fontWeight: 600, letterSpacing: ".01em" }}>
                <span>{a.low}</span><span>{a.high}</span>
              </div>
              <p style={{ fontSize: 11.5, lineHeight: 1.4, color: "rgba(55,53,47,.55)", margin: 0 }}>{a.descriptive_text}</p>
            </button>
          );
        })}
      </div>

      {missing && (
        <div style={{ marginTop: 14, background: "#fff", border: "1px dashed rgba(55,53,47,.2)", borderRadius: 7, padding: "11px 13px" }}>
          <div style={{ ...sectionLabel, marginBottom: 4 }}>What's missing</div>
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: "rgba(55,53,47,.6)" }}>{missing.descriptive_text}</p>
        </div>
      )}
    </>
  );
}
